"use client";

import Image from "next/image";
import Link from "next/link";

import { ServiceQuotaMask } from "@/components/service-quota/ServiceQuotaMask";
import { links } from "@/data/links";
import { getChartIconSrc } from "@/features/analysis/components/key-metrices-charts/data/chart-config";
import { ChartId, ChartSlug } from "@/features/analysis/types/chart";
import { Lang } from "@/i18n-config";

type Props = {
  lang: Lang;
  symbol: string;
  chartId: ChartId;
  slug: ChartSlug;
  title: string;
};

const CHART_IMAGES: Record<ChartId, string> = {
  "revenue-breakdown-chart": "/analysis/mock/revenue-breakdown-chart.png",
  "eps-chart": "/analysis/mock/eps-chart.png",
  "eps-diluted-chart": "/analysis/mock/eps-diluted-chart.png",
  "revenue-chart": "/analysis/mock/revenue-chart.png",
  "operation-income-chart": "/analysis/mock/operation-income-chart.png",
  "net-income-chart": "/analysis/mock/net-income-chart.png",
  "operating-expenses-chart": "/analysis/mock/operating-expenses-chart.png",
  "cash-flow-chart": "/analysis/mock/cash-flow-chart.png",
  "cash-and-debt-chart": "/analysis/mock/cash-and-debt-chart.png",
  "pe-ratio-chart": "/analysis/mock/pe-ratio-chart.png",
  "ps-ratio-chart": "/analysis/mock/ps-ratio-chart.png",
  "pb-ratio-chart": "/analysis/mock/pb-ratio-chart.png",
  "forward-pe-chart": "/analysis/mock/forward-pe-chart.png",
  "ev-to-ebitda-chart": "/analysis/mock/ev-to-ebitda-chart.png",
  "ev-to-sales-chart": "/analysis/mock/ev-to-sales-chart.png",
  "pe-stream-chart": "/analysis/mock/pe-stream-chart.png",
  "peg-ratio-chart": "/analysis/mock/peg-ratio-chart.png",
  "eps-surprise-chart": "/analysis/mock/eps-surprise-chart.png",
  "revenue-surprise-chart": "/analysis/mock/revenue-surprise-chart.png",
  "operating-roa-chart": "/analysis/mock/operating-roa-chart.png",
  "ros-chart": "/analysis/mock/ros-chart.png",
  "profit-margin-chart": "/analysis/mock/profit-margin-chart.png",
};

const getChartImageSrc = (chartId: ChartId) => {
  return CHART_IMAGES[chartId];
};

export const MockChartGridItem = ({
  lang,
  symbol,
  chartId,
  slug,
  title,
}: Props) => {
  const iconSrc = getChartIconSrc(chartId);
  const imageSrc = getChartImageSrc(chartId);

  return (
    <div className="relative w-full rounded-md bg-white p-5 shadow-[0_2px_6px_rgba(0,0,0,0.15)]">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="font-semibold text-gray-700">
          <span className="flex gap-2">
            <Image
              src={iconSrc}
              alt={title}
              className="relative top-[1px]"
              width={24}
              height={12}
            />
            {title}
          </span>
        </h3>
        <Link
          href={`/${lang}${links.myAnalysisFinancialMetricsChart({
            symbol,
            slug,
          })}`}
        >
          <Image src="/icons/expand.svg" width={16} height={16} alt="expand" />
        </Link>
      </div>
      <ServiceQuotaMask>
        <Image
          src={imageSrc}
          alt={title}
          width={340}
          height={230}
          className="w-full"
        />
      </ServiceQuotaMask>
    </div>
  );
};
